import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CrmHttpError,
  crmRequest,
  setCrmUnauthorizedHandler,
} from "../api/crm";

// Lightweight "is my CRM cookie still valid?" probe: GET /api/v1/auth/me
// (crm/apps/api, auth.plugin.ts). Responde { user } con la identidad del
// usuario del CRM — la guardamos para el gating de acciones por autor/rol
// (editar/borrar notas: solo su autor o un admin; el server manda, esto solo
// decide si se pintan los botones).
const CRM_SESSION_PROBE_PATH = "/auth/me";

// Subconjunto de la identidad del CRM que este SPA necesita (crm: users).
export type CrmMe = {
  email: string;
  role: "admin" | "sales" | "ops" | "viewer";
  name?: string | null;
};

// Where the user goes to establish a CRM session (Google SSO login of the CRM
// app). Opened in a new tab from the "Conectar con CRM" button; the user comes
// back and triggers recheck().
const CRM_APP_URL =
  process.env.REACT_APP_CRM_APP_URL?.replace(/\/$/, "") ??
  "http://localhost:5173";

type CrmSessionContextValue = {
  // null = still checking, true = connected, false = not connected.
  connected: boolean | null;
  // Identidad del usuario del CRM (null hasta que el probe resuelve o si no
  // hay sesión). Gating UI por autor/rol; la autorización real vive en el CRM.
  me: CrmMe | null;
  // Re-run the session probe (e.g. after the user connected in another tab).
  recheck: () => Promise<void>;
  // URL of the CRM login/app to open in a new tab.
  connectUrl: string;
};

const CrmSessionContext = createContext<CrmSessionContextValue | null>(null);

export function CrmSessionProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [me, setMe] = useState<CrmMe | null>(null);

  const recheck = useCallback(async () => {
    setConnected(null);
    try {
      const res = await crmRequest<{ user: CrmMe }>(CRM_SESSION_PROBE_PATH);
      setMe(res.user ?? null);
      setConnected(true);
    } catch (err) {
      setMe(null);
      // A 401 already fired the global handler (which sets connected=false).
      // For any other error (network, CRM down, CORS) we also treat the CRM as
      // not connected — the panels degrade to the "Conectar con CRM" state
      // rather than crashing.
      if (!(err instanceof CrmHttpError && err.status === 401)) {
        setConnected(false);
      }
    }
  }, []);

  useEffect(() => {
    // Register the global CRM 401 handler: any CRM request that 401s marks the
    // session as disconnected so gated panels show the connect affordance.
    setCrmUnauthorizedHandler(() => setConnected(false));
    // Probe once on mount.
    void recheck();
    return () => setCrmUnauthorizedHandler(null);
  }, [recheck]);

  const value = useMemo(
    () => ({ connected, me, recheck, connectUrl: CRM_APP_URL }),
    [connected, me, recheck],
  );

  return (
    <CrmSessionContext.Provider value={value}>
      {children}
    </CrmSessionContext.Provider>
  );
}

export function useCrmSession(): CrmSessionContextValue {
  const ctx = useContext(CrmSessionContext);
  if (!ctx) {
    throw new Error("useCrmSession must be used inside CrmSessionProvider");
  }
  return ctx;
}

/**
 * Wraps CRM-backed panels. Renders children only once a CRM session is
 * confirmed; otherwise shows a "checking…" placeholder or a card with a
 * "Conectar con CRM" button that opens the CRM login in a new tab and lets the
 * user re-check afterwards.
 */
export function CrmConnectGate({ children }: { children: ReactNode }) {
  const { connected, recheck, connectUrl } = useCrmSession();

  if (connected === null) {
    return (
      <div style={{ padding: "1rem", color: "#52606d", fontSize: "0.9rem" }}>
        Cargando…
      </div>
    );
  }

  if (!connected) {
    return (
      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "0.6rem",
          background: "white",
          padding: "1.25rem",
          color: "#1f2933",
          maxWidth: "28rem",
        }}
      >
        <p style={{ margin: "0 0 0.85rem", fontSize: "0.95rem" }}>
          Necesitas conectar con el CRM para ver esta sección.
        </p>
        <button
          type="button"
          onClick={() => {
            window.open(connectUrl, "_blank", "noopener,noreferrer");
          }}
          style={{
            borderRadius: "0.4rem",
            background: "#0069d9",
            border: "1px solid #0069d9",
            color: "white",
            padding: "0.5rem 0.9rem",
            fontSize: "0.9rem",
            cursor: "pointer",
            marginRight: "0.6rem",
          }}
        >
          Conectar con CRM
        </button>
        <button
          type="button"
          onClick={() => void recheck()}
          style={{
            borderRadius: "0.4rem",
            background: "white",
            border: "1px solid #dee2e6",
            color: "#1f2933",
            padding: "0.5rem 0.9rem",
            fontSize: "0.9rem",
            cursor: "pointer",
          }}
        >
          Ya he conectado
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
